import { graphql } from 'react-apollo';

import { branch, compose, renderNothing } from 'recompose';

import Retracked from 'js/app/retracked';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import Naptime from 'bundles/naptimejs';
import EnterprisePermittedProductsV1 from 'bundles/naptimejs/resources/enterprisePermittedProducts.v1';
import { adaptEnterpriseProductsWithLabs } from 'bundles/nostos-external/adapters';
import { NOSTOS_EXTERNAL_TABLE_IDS } from 'bundles/nostos-external/constants';
import type {
  NostosExternalGetDataQueryType,
  NostosExternalGetDataQueryVariables,
} from 'bundles/nostos-external/queries';
import { NostosExternalGetDataQuery } from 'bundles/nostos-external/queries';

type PropsFromCaller = {
  productId: string;
  isS12n?: boolean;
  primaryLanguages: Array<string>;
  isMobile?: boolean;
  iconColor?: string;
};

type InnerProps = {
  isMobile?: boolean;
  iconColor?: string;
};

type PropsFromHasCourseraLabsQuery = {
  hasLabAssignments?: boolean;
};

type PropsFromEnterprisePermittedProductsQuery = {
  permittedProducts: {
    accessType: 'BLACKLIST' | 'WHITELIST';
    productIds: Array<string>;
  };
};

type PropsRequiredByWrapper = PropsFromCaller &
  PropsFromHasCourseraLabsQuery &
  PropsFromEnterprisePermittedProductsQuery;

export const shouldHidePromoUnit = ({
  primaryLanguages,
  productId,
  isS12n,
  permittedProducts,
  hasLabAssignments,
}: PropsRequiredByWrapper): boolean => {
  const id = `${isS12n ? 'Specialization' : 'VerifiedCertificate'}~${productId}`;

  // Prevent error when permittedProducts is undefined
  let isPermittedInC4B: boolean;
  if (!permittedProducts) {
    isPermittedInC4B = false;
  } else {
    isPermittedInC4B =
      permittedProducts.accessType === 'BLACKLIST'
        ? !permittedProducts.productIds.includes(id)
        : permittedProducts.productIds.includes(id);
  }

  // only show promo units that:
  //  * XDP is in English
  //  * offered by C4B for now
  //  * not have labs
  const isVisible = primaryLanguages?.includes('en') && isPermittedInC4B && !hasLabAssignments;
  return !isVisible;
};

const withEnterprisePermittedProducts = Naptime.createContainer(() => ({
  permittedProducts: EnterprisePermittedProductsV1.get('LegacyGlobalScopeId', { fields: ['accessType', 'productIds'] }),
}));

const withTrackingNamespace = Retracked.createTrackedContainer(() => {
  const namespace = {
    app: 'enterprise',
    page: 'xdp',
  };

  return {
    namespace,
  };
});

const withEnterprisePromoWrapper = compose<InnerProps, PropsFromCaller>(
  withTrackingNamespace,
  withEnterprisePermittedProducts,
  graphql<
    PropsFromCaller,
    NostosExternalGetDataQueryType,
    NostosExternalGetDataQueryVariables,
    PropsFromHasCourseraLabsQuery
  >(NostosExternalGetDataQuery, {
    options: ({ productId: courseId }) => ({
      variables: {
        input: {
          tableId: NOSTOS_EXTERNAL_TABLE_IDS.enterprise_products_with_labs,
          keys: [tupleToStringKey(['VerifiedCertificate', courseId])],
        },
      },
      context: { clientName: 'gatewayGql' },
      // Matches the `useNostosExternalData` invariant: keep partial data when the
      // gateway also returns errors, so a miss on one key cannot discard the rest.
      errorPolicy: 'all',
    }),
    props: ({ data }) => {
      const elements = adaptEnterpriseProductsWithLabs(data?.NostosExternal?.getData?.entries);
      return {
        hasLabAssignments: elements?.[0]?.content?.hasLabAssignments,
      };
    },
  }),
  branch<PropsRequiredByWrapper>(shouldHidePromoUnit, renderNothing)
);

export default withEnterprisePromoWrapper;
